import React from 'react';
import { graphql } from 'gatsby';
import Layout from '../components/Layout';
import PageBanner from '../components/PageBanner';
import Section from '../components/Section';
import { Container, Row, Column } from '../components/Grid';
import Image from '../components/Image';
import Modal from '../components/Modal';
import Video from '../components/Video';
import Button from '../components/Button';
import OutboundLink from '../components/OutboundLink';
import AboutPartners from '../components/AboutPartners';
import './promise.scss';

const PromisePage = ({ data }) => {
  const page = data.pagesYaml;

  return (
    <Layout title={page.title} description={page.metaDescription}>
      <PageBanner title={page.title} />

      {/* ── Intro: poster left, text right ─────────────────────────── */}
      <Section>
        <Container>
          <div className="promise-intro">
            <div className="promise-intro__image">
              <Image filename="thmb-intro.jpg" alt="The Promise film poster" />
            </div>
            <div className="promise-intro__body">
              <h2 className="promise-intro__title">The Promise Film</h2>
              <p>
                Using films can be an important way to help students make connections
                to the past. <em>The Promise</em> is a historical fiction film that
                conveys a multi-perspective history of the Armenian Genocide. As the
                plot of <em>The Promise</em> unfolds in the Ottoman Empire during
                1914, on the brink of its entrance into the First World War and the
                Armenian Genocide, viewers discover the symbolic love between friends,
                family, and one's national identity. <em>The Promise</em> shares the
                story of Ottoman Armenian people and the brave men and women who risked
                their lives to help save them from complete annihilation.
              </p>
              <Modal
                video
                trigger={
                  <Button className="promise-trailer-btn">Watch the Trailer</Button>
                }
              >
                <Video
                  src="https://app.discoveryeducation.com/learn/embedded-player/55ce72e0-fe59-4cbc-bedc-48d0451a41fb?utm_source=microsite&utm_medium=embed"
                  title="The Promise — Official Trailer"
                />
              </Modal>
              <p className="promise-intro__ext-link">
                <OutboundLink
                  to="https://thepromisetoact.org/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Visit ThePromisetoAct.org to learn more about the film →
                </OutboundLink>
              </p>
            </div>
          </div>
        </Container>
      </Section>

      {/* ── Classroom and Family Activities ────────────────────────── */}
      <Section>
        <Container>
          <h2 className="promise-section-heading">Classroom and Family Activities</h2>
          <div className="promise-activity-grid">
            {/* Classroom card */}
            <div className="promise-activity-card">
              <div className="promise-activity-card__image">
                <Image filename="thmb-classroom.jpg" alt="Classroom Activity" />
              </div>
              <div className="promise-activity-card__body">
                <h3 className="promise-activity-card__title">Classroom Activities</h3>
                <p>
                  Designed for middle and high school students, these classroom
                  activities guide students through exploration of themes related to
                  the Armenian Genocide like "us vs. them" and the role different
                  stakeholders played in publicizing the genocide and providing
                  relief — ending in exploration of current examples of U.S.
                  involvement in other countries experiencing crisis, conflict,
                  and/or genocide.
                </p>
                <div className="promise-activity-card__downloads">
                  <Button
                    to="/pdf/USCSF-StudentActivity-MiddleSchool.pdf"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Middle School Student Activity
                  </Button>
                  <Button
                    to="/pdf/USCSF-StudentActivity-HIghSchool.pdf"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    High School Student Activity
                  </Button>
                </div>
              </div>
            </div>

            {/* Family card */}
            <div className="promise-activity-card">
              <div className="promise-activity-card__image">
                <Image filename="thmb-family.jpg" alt="Family Activity" />
              </div>
              <div className="promise-activity-card__body">
                <h3 className="promise-activity-card__title">Family Activity</h3>
                <p>
                  This guide contains support for you and your family as you learn
                  more about the Armenian Genocide and the lessons that we can take
                  from it today. This activity follows a Discuss, Watch, Act format
                  to guide your family in making connections between the past, and
                  current events, to find ways to take action to promote
                  understanding, compassion, and remembrance.
                </p>
                <div className="promise-activity-card__downloads">
                  <Button
                    to="/pdf/USCSF-FamilyActivity-ThePromise.pdf"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Family Activity
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </Container>
      </Section>

      {/* ── Supporting Testimony Clips ─────────────────────────────── */}
      <Section>
        <Container>
          <h2 className="promise-section-heading">Supporting Testimony Clips</h2>
          <ul className="promise-testimony-list">
            <li>Aivazian remembers his grandmother's reaction to a Turkish neighbor who proposed that his family should convert to Islam to avoid deportation in the Armenian Genocide.</li>
            <li>Hagop speaks of the deaths of his family members and expresses his personal pain and frustration that the denial of the Armenian Genocide causes him.</li>
            <li>Baronian describes meeting a young Armenian woman and discovering that she and her father had survived by pretending to be Turks.</li>
            <li>Albert talks about being the only doctor for civilians after WWI, serving Armenian orphans in Izmit, Turkey and then being sent to Talas, Turkey where he helped 2,700 Christian orphans leave the area.</li>
            <li>Arshag recalls how doctors at an American hospital saved many Armenians by giving them medication and food.</li>
            <li>Dr. Hovannisian explains that Turkish leaders used propaganda to dehumanize Armenians and convince the Muslim majority that they needed to be eliminated.</li>
            <li>Hovannisian explains how social Darwinism informed the genocidal practices of the Turkish regime during the Armenian Genocide.</li>
            <li>Marie discusses how Armenians were portrayed as inferior by the Turks to create divisions in society.</li>
            <li>Mardiganian explains that the Turks wanted to massacre the Armenians for two reasons: they wanted their land, and they wanted to wipe out Christianity.</li>
            <li>Haiastan shares how Leslie Davis, US Consul to Harput, hid many Armenians from the Harput province in the American Consulate during the time of the Armenian Genocide.</li>
          </ul>
        </Container>
      </Section>

      <AboutPartners />
    </Layout>
  );
};

export default PromisePage;

export const query = graphql`
  query PromisePageQuery($id: String!) {
    pagesYaml(id: { eq: $id }) {
      slug
      path
      title
      metaDescription
    }
  }
`;
